import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, RefreshCw, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/Button";

interface CameraCaptureProps {
  onCapture: (blob: Blob) => void;
}

export function CameraCapture({ onCapture }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      setError(null);
      stopTracks();
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 640 } },
        audio: false,
      });
      // Kalau komponen sudah unmount saat await, langsung stop track
      if (!mountedRef.current) {
        mediaStream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch {
      if (mountedRef.current) {
        setError("Gagal mengakses kamera. Pastikan izin kamera telah diberikan.");
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [stopTracks]);

  useEffect(() => {
    mountedRef.current = true;
    startCamera();
    return () => {
      mountedRef.current = false;
      inFlightRef.current = false;
      stopTracks();
    };
  }, [startCamera, stopTracks]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context || !video.videoWidth || !video.videoHeight) return;

    // Set canvas dimension max 640px untuk hemat bandwidth
    const size = Math.min(video.videoWidth, video.videoHeight, 640);
    canvas.width = size;
    canvas.height = size;

    const startX = (video.videoWidth - size) / 2;
    const startY = (video.videoHeight - size) / 2;

    // Mirror horizontal canvas agar sesuai dengan yang dilihat user di preview kamera
    context.translate(size, 0);
    context.scale(-1, 1);

    context.drawImage(video, startX, startY, size, size, 0, 0, size, size);

    // L-2: Hanya pakai toBlob (memory efficient), hapus double-encode toDataURL.
    canvas.toBlob((blob) => {
      if (!blob || !mountedRef.current) return;
      const url = URL.createObjectURL(blob);
      setCapturedImage(url);
      onCapture(blob);
    }, "image/jpeg", 0.85);

    stopTracks();
  };

  const handleRetake = async () => {
    if (capturedImage) URL.revokeObjectURL(capturedImage);
    stopTracks();
    if (videoRef.current) videoRef.current.srcObject = null;
    setCapturedImage(null);
    await startCamera();
  };

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <div className="relative w-full max-w-[320px] aspect-square rounded-2xl overflow-hidden bg-slate-900 border-2 border-slate-200 flex items-center justify-center">
        {capturedImage ? (
          <img src={capturedImage} alt="Selfie preview" className="w-full h-full object-cover" />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover scale-x-[-1]"
          />
        )}

        <canvas ref={canvasRef} className="hidden" />

        {error && (
          <div className="absolute inset-0 bg-slate-900/90 flex items-center justify-center p-4 text-center text-xs text-red-400">
            {error}
          </div>
        )}
      </div>

      <div>
        {capturedImage ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRetake} className="gap-1 text-xs">
              <RefreshCw className="w-3.5 h-3.5" /> Ulangi Foto
            </Button>
            <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium px-2 py-1 bg-emerald-50 rounded">
              <CheckCircle2 className="w-3.5 h-3.5" /> Siap!
            </div>
          </div>
        ) : (
          <Button onClick={handleCapture} disabled={!!error} className="gap-1.5 text-xs">
            <Camera className="w-4 h-4" /> Ambil Foto Selfie
          </Button>
        )}
      </div>
    </div>
  );
}